import React, {useMemo} from "react";

import User, {talentUpdateRequest} from "../../../models/User";
import UserImageCircle from "../../../components/UserImageCircle";
import {Badge, Checkbox, Dropdown, Switch} from "antd";
import {useIsMutating, useMutation, useQueryClient} from "@tanstack/react-query";
import { useWindowSize } from 'react-use';
import {useAuth} from "../../../providers/AuthProvider";
import InfoTooltip from "../../../components/InfoTooltip";
import {Link, useNavigate} from "react-router-dom";


const SupplierTalentResourceCard = ({isDisabled, talent, ...props}) => {
    const queryClient = useQueryClient();
    const {auth, update: updateAuth} = useAuth();
    const { width: windowWidth } = useWindowSize();
    const updateClientOrg = (data) => {
        return updateAuth({
            ...auth,
            client_organization: {
                ...auth.clientOrg(),
                total_supplied_talent_users: data.length,
                total_supplied_matching_talent_users: TB.filter(data, {'is_available': true, offers_byo_work: true})?.length,
                total_supplied_team_talent_users: TB.filter(data, {'is_available': true, offers_service_work: true})?.length
            }
        })
    }
    const navigate = useNavigate();

    const isMutatingTalent = useIsMutating(['talentUpdate'])

    const updateTalentMutation = useMutation(['talentUpdate'], (params) => {
            return talentUpdateRequest(params, `/api/clients/${auth.supplierOrg()?.id}/users/${talent.id}/update`)
        }, {
            onSuccess: (user) => {
                if (user) {
                    queryClient.setQueryData(['fetchAllSupplierTalentResources', auth.clientOrg?.()?.id], prevData => {
                        let recordIndex = TB.findIndex(prevData, {id:  user.id});

                        let updatedData = TB.set([...prevData], recordIndex, user);
                        updateClientOrg(updatedData);
                        return updatedData;
                    });
                    queryClient.invalidateQueries('fetchSupplierDashboard')

                    localStorage.removeItem('jobBoardParams')
                    return TB.toast('Talent Resource Availability Updated')
                }

            },
            onError: (error) => {
                return TB.showServerErrorToast();
            }
        }
    )
    const talentAccountActionMutation = useMutation(['toggleTalentDeactivate'], (talentMethod) => {
            return talent[talentMethod]?.();
        }, {
            onSuccess: (user) => {
                if (user) {
                    let clientOrg = {...auth.client_organization};

                    queryClient.setQueryData(['fetchAllSupplierTalentResources', auth.clientOrg?.()?.id], prevData => {
                        let recordIndex = TB.findIndex(prevData, {id:  user.id});

                        let updatedData = TB.set([...prevData], recordIndex, user);
                        updateClientOrg(updatedData);
                        return updatedData;
                    });
                    queryClient.invalidateQueries('fetchSupplierDashboard')

                    localStorage.removeItem('jobBoardParams')
                    return TB.toast('Talent Resource Account ' + user.isDeactivated() ? 'Deactivated' : ' Restored')
                }
            },
            onError: (error) => {
                return TB.showServerErrorToast();
            }
        }
    )

    const actionsDisabled = talentAccountActionMutation.isLoading || updateTalentMutation.isLoading || isMutatingTalent > 0 || isDisabled;

    const menuItems = useMemo(() => {
        let items = [];

        if (talent.can_be_managed && !talent.isDeactivated()) {
            items.push({
                label: 'Edit Default Rate/Preferences',
                key: 'edit-profile',
                disabled: actionsDisabled,
                handle: () => {
                    return navigate(`edit/${talent.id}`)
                }
            })
        }

        if (talent.can_be_managed && talent.isRegistered()) {
            items.push({
                label: talent.isDeactivated() ? 'Restore Account' : 'Deactivate Account',
                danger: true,
                key: 'deleteRestore',
                disabled: actionsDisabled,
                handle: async () => {
                    if (!talent.isDeactivated()) {
                        let didConfirm = await TB.sysAlert('client.confirm_deactivate_user', {
                            name: talent.fullName()
                        })

                        if (!didConfirm) {
                            return;
                        }
                    }
                    return talentAccountActionMutation.mutate(talent.isDeactivated() ? 'restore' : 'deactivate')
                }
            })
        }

        if (talent.isRegistered() && !talent.isDeactivated()) {
            items.push({
                label: 'View Client-facing Profile',
                key: 'client-profile',
                disabled: actionsDisabled,
                handle: () => {
                    return navigate(`/talent-profiles/${talent.id}`)
                }
            })
        } else if (talent.can_be_invited && !talent.isRegistered() && !talent.isDeactivated()) {
            items.push({
                label: 'Resend Invite',
                key: 'resend-invite',
                disabled: actionsDisabled,
                handle: () => {
                    return talentAccountActionMutation.mutate('resendInvite')
                }
            })
        }

        return items;
    }, [talent.status, talentAccountActionMutation.isLoading, isMutatingTalent, isDisabled, updateTalentMutation.isLoading]);
    const menuProps = {
        items: TB.map(menuItems, item => TB.pick(item, ['label', 'key', 'disabled'])),
        onClick: ({item, key}) => {
            let menuItem = TB.find(menuItems, ['key', key]);
            if (menuItem && menuItem.handle) {
                return menuItem.handle()
            }
        }
    };

    const lastUpdatedLabel = useMemo(() => {
        if (!talent.is_active) return false;

        return TB.moment(talent.updated_at).fromNow();

    }, [talent.is_active, talent?.updated_at, talent.id]);

    let label = User.labelForStatus(talent.status);

    let badgeStatus;
    switch (label) {
        case 'Pending Registration':
            badgeStatus = 'processing';
            break;
        case 'Registered':
            badgeStatus = 'success';
            break;
        case 'Inactive':
            badgeStatus = 'error';
            break;
    }


    // NOTE: rendering two different card designs based on screen width

    // mobile screen size
    if (windowWidth < 768) {
        return (
            <div
                className={TB.mapClass("talent-member-card flex flex-row items-stretch justify-between bg-white gap-4 px-4 py-4 sm:px-8 sm:py-6", {
                    pendingInvite: !talent.isRegistered()
                })}
            >
                <div className="w-full flex flex-col gap-3">
                    <div className="flex justify-between">
                        <div className="flex gap-4 items-center">
                            <UserImageCircle user={talent} size={70} className="self-stretch img-grayscale"/>

                            <div>
                                <div className="flex items-center gap-3 relative">

                                    <div className="max-w-1/3">
                                        <p className="text-lg text-black font-semibold text-ellipsis whitespace-nowrap overflow-hidden">
                                            {talent.firstNamePlusInitial?.()}
                                        </p>
                                    </div>

                                    <div className="flex flex-row items-center gap-2 flex-nowrap" id={`talent-status-${talent.id}`}>
                                        <Badge status={badgeStatus}/>
                                        <span className="text-xs">
                                            {label}
                                        </span>
                                    </div>

                                </div>
                                <div className="flex items-center gap-1.5">
                                    <a href={`mailto:${talent.email}`} target="_blank" className="text-xxs text-[#913ef0] font-medium hover:underline text-ellipsis whitespace-nowrap overflow-hidden">
                                        {talent.email}
                                    </a>
                                </div>
                            </div>
                        </div>
                        <Dropdown menu={menuProps} className="p-0 clickable" trigger="click">
                            <i className="fas fa-2x fa-ellipsis-v text-gray"></i>
                        </Dropdown>
                    </div>

                    <div>
                        <div className="flex items-center gap-x-3 gap-y-1 flex-wrap">
                            <div className="flex items-center gap-1.5 max-w-[300px]">
                                <i className="text-xxs fas fa-brain text-[#BFC5CB]"/>
                                <p className="text-xxs font-medium text-ellipsis whitespace-nowrap overflow-hidden">
                                    {talent.job_title || 'N/A'}
                                </p>
                            </div>
                            <div className="flex items-center gap-1.5 max-w-[300px]">
                                <i className="text-xxs fas fa-map-marker text-[#BFC5CB]"/>
                                <p className="text-xxs font-medium text-ellipsis whitespace-nowrap overflow-hidden">
                                    {talent.location?.full || <i>Not set</i>}
                                </p>
                            </div>
                        </div>

                        {talent.is_active && (
                            talent.bio ? (
                                <div className="talent-member-bio" dangerouslySetInnerHTML={{__html: talent.bio}} />
                            ) : (
                                <div className="talent-member-bio"><small><i className="opacity-50">Bio missing</i></small></div>
                            )
                        )}

                        <div className="flex gap-3 items-start justify-between">
                            <div className="flex gap-2">
                                {talent.is_active && (
                                    <Link to={`/supplier/talent-resources/${talent.id}`}
                                        className="btn btn-link btn-link-primary whitespace-nowrap">
                                        Edit Profile
                                    </Link>
                                )}
                                {lastUpdatedLabel && (
                                    <div className="ml-auto flex items-center gap-0.5">
                                        <span className="text-xxs text-[#BFC5CB]">Last Active:</span>
                                        <p className="text-xxs whitespace-nowrap overflow-hidden text-[#BFC5CB]">
                                            {lastUpdatedLabel}
                                        </p>
                                    </div>
                                )}
                            </div>

                            <div className="talent-default-rate pt-0" id={`talent-rate-${talent.id}`}>
                                {talent.default_hourly_rate ? TB.formatCurrency(talent.default_hourly_rate) : '$--'}
                                <span>/hr</span>
                            </div>
                        </div>
                    </div>

                    <div className="flex justify-between items-center">
                        <div className="flex flex-col items-start">
                            <Checkbox
                                className="m-0"
                                checked={talent.offers_byo_work}
                                disabled={updateTalentMutation.isLoading || !talent.is_active || !talent.is_available}
                                onChange={(checked, event) => {
                                    if (actionsDisabled) {
                                        return
                                    }

                                    return updateTalentMutation.mutate({
                                        offers_byo_work: !talent.offers_byo_work
                                    })
                                }}
                            >
                                <p className="text-xxs">
                                    Available for hire by role
                                </p>
                            </Checkbox>
                            <Checkbox
                                className="m-0"
                                checked={talent.offers_service_work}
                                disabled={updateTalentMutation.isLoading || !talent.is_active || !talent.is_available}
                                onChange={(checked, event) => {
                                    if (actionsDisabled) {
                                        return
                                    }

                                    return updateTalentMutation.mutate({
                                        offers_service_work: !talent.offers_service_work
                                    })
                                }}
                            >
                                <p className="text-xxs">
                                    Available for hire by team
                                </p>
                            </Checkbox>
                        </div>

                        <div>
                            <Switch
                                checkedChildren="Available"
                                unCheckedChildren="Unavailable"
                                disabled={!talent.is_active}
                                checked={talent.is_active && talent.is_available}
                                onChange={(checked, event) => {
                                    if (actionsDisabled) {
                                        return
                                    }

                                    return updateTalentMutation.mutate({
                                        is_available: !talent.is_available
                                    })
                                }}
                                loading={updateTalentMutation.isLoading}
                            />
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    return (
        <div
            className={TB.mapClass("talent-member-card flex flex-row items-stretch justify-between bg-white gap-4 px-4 py-4 md:px-8 md:py-6", {
                pendingInvite: !talent.isRegistered()
            })} key={talent.id}>
            <div className="flex flex-col items-stretch gap-2.5">
                <UserImageCircle user={talent} size={100} className="self-stretch img-grayscale"/>
            </div>


            <div className="flex-grow flex flex-col gap-1.5">
                <div className="mb-2">
                    <div className="flex flex-row items-center gap-3 relative">
                        <div className="max-w-1/3">
                            <p className="text-lg text-black font-semibold text-ellipsis whitespace-nowrap overflow-hidden">
                                {talent.firstNamePlusInitial?.()}
                            </p>
                        </div>
                        <div className="flex flex-row items-center gap-2 flex-nowrap" id={`talent-status-${talent.id}`}>
                            <Badge status={badgeStatus}/>
                            <span className="text-xs">
                                {label}
                            </span>
                        </div>

                        <div className="ml-auto flex flex-col items-start scale-75">
                            <Checkbox checked={talent.is_available && talent.offers_byo_work}
                                      disabled={updateTalentMutation.isLoading || !talent.is_active || !talent.is_available}
                                      onChange={(checked, event) => {
                                          if (actionsDisabled) {
                                              return
                                          }

                                          return updateTalentMutation.mutate({
                                              offers_byo_work: !talent.offers_byo_work
                                          })
                                      }}>
                                <p>
                                    Available for hire by role
                                </p>
                            </Checkbox>
                            <div>
                                <Checkbox checked={talent.is_available && talent.offers_service_work}
                                          disabled={updateTalentMutation.isLoading || !talent.is_active || !talent.is_available}
                                          onChange={(checked, event) => {
                                              if (actionsDisabled) {
                                                  return
                                              }

                                              return updateTalentMutation.mutate({
                                                  offers_service_work: !talent.offers_service_work
                                              })
                                          }}>
                                    <p>
                                        Available for hire by team
                                    </p>
                                </Checkbox>
                            </div>
                        </div>

                        <div>
                            <Switch
                                checkedChildren="Available"
                                unCheckedChildren="Unavailable"
                                disabled={!talent.is_active}
                                checked={talent.is_active && talent.is_available}
                                onChange={(checked, event) => {
                                    if (actionsDisabled) {
                                        return
                                    }

                                    return updateTalentMutation.mutate({
                                        is_available: !talent.is_available
                                    })
                                }}
                                loading={updateTalentMutation.isLoading}/>
                        </div>
                    </div>

                    <div className="flex flex-row items-center gap-1.5">
                        <a href={`mailto:${talent.email}`} target="_blank"
                           className="text-xxs text-[#913ef0] font-medium hover:underline text-ellipsis whitespace-nowrap overflow-hidden">
                            {talent.email}
                        </a>
                    </div>
                </div>

                <div>
                    <div className="flex flex-row items-center gap-3">
                        <div className="flex flex-row items-center gap-1.5 max-w-[300px]">
                            <i className="text-xxs fas fa-brain text-[#BFC5CB]"/>
                            <p className="text-xxs font-medium text-ellipsis whitespace-nowrap overflow-hidden">
                                {talent.job_title || 'N/A'}
                            </p>
                        </div>
                        <div className="flex flex-row items-center gap-1.5 max-w-[300px]">
                            <i className="text-xxs fas fa-map-marker text-[#BFC5CB]"/>
                            <p className="text-xxs font-medium text-ellipsis whitespace-nowrap overflow-hidden">
                                {talent.location?.full || <i>Not set</i>}
                            </p>
                        </div>
                    </div>


                    {talent.is_active && (
                        talent.bio ? (
                            <div className="talent-member-bio" dangerouslySetInnerHTML={{__html: talent.bio}}/>
                        ) : (
                            <div className="talent-member-bio"><small><i className="opacity-50">Bio missing</i></small>
                            </div>
                        )
                    )}
                </div>

                <div className="flex flex-row gap-3 items-start justify-between">
                    <div className="flex flex-row gap-2">
                        {talent.is_active && (
                            <Link to={`/supplier/talent-resources/${talent.id}`}
                                  className="btn btn-link btn-link-primary whitespace-nowrap">
                                Edit Profile
                            </Link>
                        )}
                        {lastUpdatedLabel && (
                            <div className="ml-auto flex flex-row items-center gap-0.5">
                                <span className="text-xxs text-[#BFC5CB]">Last Active:</span>
                                <p className="text-xxs whitespace-nowrap overflow-hidden text-[#BFC5CB]">
                                    {lastUpdatedLabel}
                                </p>
                            </div>
                        )}
                    </div>

                    <div className="talent-default-rate" id={`talent-rate-${talent.id}`}>
                        {talent.default_hourly_rate ? TB.formatCurrency(talent.default_hourly_rate) : '$--'}
                        <span>/hr</span>
                    </div>
                </div>

                {talent.is_active && (
                    <InfoTooltip target={`talent-rate-${talent.id}`} placement="top" type="dark">
                        <span className="text-white">Default Hourly Rate</span>
                    </InfoTooltip>
                )}
                <InfoTooltip target={`talent-status-${talent.id}`} placement="top" type="dark">
                    <span className="text-white">
                        {talent.is_active ? (
                            <span>Active since: {TB.moment(talent.registered_at).format('lll')}</span>
                        ) : (
                            <span>Invite sent: {TB.moment(talent.invited_at).format('lll')}</span>
                        )}
                    </span>
                </InfoTooltip>

                {/*<div className="discipline-skills-container skill-importance-container">*/}
                {/*    {talent.talent_disciplines?.length > 0 ? talent.talent_disciplines.map((discipline, i) => (*/}
                {/*        <SkillFormButton*/}
                {/*            key={`${discipline.name}`}*/}
                {/*            className="has-skill"*/}
                {/*            valuePropName="hourly_rate"*/}
                {/*            disabled={true}*/}
                {/*            scoringOptions={null}*/}
                {/*            skill={{...discipline, hourly_rate: `$${discipline.hourly_rate}/hr`}}*/}
                {/*            selected={true}*/}
                {/*        />*/}
                {/*    )) : 'No Roles Added Yet'}*/}
                {/*</div>*/}


            </div>

            <div className="flex flex-col items-end justify-start gap-3">
                <Dropdown menu={menuProps} className="p-0 clickable" trigger="click">
                    <i className="fas fa-2x fa-ellipsis-v text-gray"></i>
                </Dropdown>
            </div>
        </div>
    )
}

export default SupplierTalentResourceCard;